import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { AutoSizer, List } from 'react-virtualized';

import { AssetLoader, AssetThumbnail, GridSkeleton } from './AssetThumbnailViewHelper';
import { fetchSpaceAssetsData } from '../viewHelper';
import { extractTextFromJSX } from '../../SpaceAssetsSelectorModal/helper';
import { SpaceAssetsEmptyState } from '../EmptyState';

import {
  SPACE_ASSETS_GRID_LIMIT,
  SPACE_ASSETS_GRID_LIMIT_LOADING,
  SPACE_ASSETS_THUMBNAIL_HEIGHT,
  SPACE_ASSETS_VIEW_TYPE
} from '../../../utils/constants';
import { AISuggestions, AssetData, AssetValidationFilter } from '../../../utils/types';

import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';

import '../viewComponent.scss';
import { useQueryBuilder } from '../../SpaceAssetsSelectorModal/context/QueryBuilderContext';

const LOAD_THRESHOLD = 0.8;

const useDisplayScaleFactor = (): number => {
  const [displayScaleFactor, setDisplayScaleFactor] = useState(1);

  useEffect(() => {
    const baseFontSize = 16;
    const currentFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    setDisplayScaleFactor(currentFontSize / baseFontSize);

    const handleDisplayScaleChange = debounce(() => {
      const updatedFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const updatedScaleFactor = updatedFontSize / baseFontSize;

      const hasSignificantChange = Math.abs(updatedScaleFactor - displayScaleFactor) > 0.01;
      if (hasSignificantChange) {
        setDisplayScaleFactor(updatedScaleFactor);
      }
    }, 100);

    window.addEventListener('resize', handleDisplayScaleChange);

    return (): void => {
      window.removeEventListener('resize', handleDisplayScaleChange);
      handleDisplayScaleChange.cancel();
    };
  }, [displayScaleFactor]);

  return displayScaleFactor;
};

interface ThumbnailViewProps {
  linkedSpaces: string[];
  setMessageComponent: (message: ReactNode) => void;
  sortByData: { id: string; sortingDirection: "asc" | "desc" };
  spaceAssetsSearchOptions: { searchType: string | React.ReactNode; searchText: string };
  breadcrumbClickHandler: (obj: any) => void;
  breadcrumbPath: any[];
  setBreadcrumbPath: (path: any) => void;
  selectedFolder: AssetData | null;
  setSelectedFolder: (folder: AssetData) => void;
  onParamsChange: (params: { page?: string; search?: string; rowsPerPage?: string }) => void;
  isMultiple: boolean;
  spaceAssetsPicked: AssetData[];
  setSpaceAssetsPicked: (assets: AssetData[]) => void;
  setShouldClearSearchFilters: (shouldClearSearchFilters: boolean) => void;
  handleGoBack: () => void;
  excludeUids: string[];
  isAPIError: boolean;
  setIsAPIError: (isAPIError: boolean) => void;
  ai_suggestions: AISuggestions;
  isSuggestions: boolean;
  setShouldDisabledActionPanel: (shouldDisabledActionPanel: boolean) => void;
  totalCounts: number;
  setTotalCounts: (totalCounts: number) => void;
  showSelectedAssets: boolean;
  isResponsive: boolean;
  appliedFilterCount?: number;
  localesWithFallback: string[];
  linkedWorkspaces: { workspace: string; space_uid: string }[];
  fetchSpaceAssets: (params: any, headers: any, options: any) => Promise<any>;
  validationFilter?: AssetValidationFilter;
  spacesWithTitles?: Array<{ uid: string; title: string }>;
  isMoveTarget?: boolean;
  isUploadTarget?: boolean;
  isNewlyCreatedFolder?: boolean;
  onSuggestionsFetchSettled?: (success: boolean) => void;
  disablePrivateAssets?: boolean;
}

const ThumbnailView: React.FC<ThumbnailViewProps> = (props) => {
  const {
    sortByData,
    spaceAssetsPicked,
    setSpaceAssetsPicked,
    spaceAssetsSearchOptions,
    breadcrumbClickHandler,
    breadcrumbPath,
    setBreadcrumbPath,
    selectedFolder,
    isMultiple,
    setShouldClearSearchFilters,
    handleGoBack,
    isAPIError,
    setIsAPIError,
    ai_suggestions,
    isSuggestions,
    setShouldDisabledActionPanel,
    totalCounts,
    setTotalCounts,
    showSelectedAssets,
    isResponsive = false,
    appliedFilterCount,
    localesWithFallback,
    linkedWorkspaces = [],
    fetchSpaceAssets,
    validationFilter,
    spacesWithTitles = [],
    isMoveTarget = false,
    isUploadTarget = false,
    isNewlyCreatedFolder = false,
    onSuggestionsFetchSettled,
    disablePrivateAssets = false,
  } = props;
  const [loading, setLoading] = useState(true);
  const [assetData, setAssetData] = useState<AssetData[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentItemsPerRow, setCurrentItemsPerRow] = useState(0);
  const displayScaleFactor = useDisplayScaleFactor();

  const { queryBuilder } = useQueryBuilder();

  const displayData = assetData;

  const displayTotalCounts = isMoveTarget
    ? displayData.length
    : totalCounts;
  const selectedAssetUids = useMemo(
    () => new Set(spaceAssetsPicked.map((asset) => asset.uid)),
    [spaceAssetsPicked]
  );

  // Stable string so react-virtualized repaints rows after selection changes (Grid caches cells).
  const pickedUidsSignature = useMemo(
    () =>
      [...spaceAssetsPicked]
        .map((a) => a.uid)
        .sort()
        .join(','),
    [spaceAssetsPicked]
  );

  const scaledRowHeight = useMemo(() => SPACE_ASSETS_THUMBNAIL_HEIGHT * displayScaleFactor, [displayScaleFactor]);

  const listRef = useRef<List>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const lastLoadedRow = useRef(0);
  const loadingMoreRef = useRef(false);
  // One AbortController per query context — a fresh query (skip 0) aborts the prior
  // one so in-flight Lottie sniffs cancel on query/search change; pagination reuses it.
  const lottieAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.forceUpdateGrid();
    }
  }, [displayScaleFactor]);

  useLayoutEffect(() => {
    listRef.current?.forceUpdateGrid();
  }, [pickedUidsSignature]);

  const fetchData = useCallback(() => {
    if (loading || showSelectedAssets) return;

    if (!isInitialLoad) {
      loadingMoreRef.current = true;
    }
    if (assetData.length === 0) {
      lottieAbortRef.current?.abort();
      lottieAbortRef.current = new AbortController();
    }
    return fetchSpaceAssetsData(
      queryBuilder,
      {
        sortBy: { id: sortByData.id, sortingDirection: sortByData.sortingDirection },
        searchText: spaceAssetsSearchOptions.searchText,
        searchType: extractTextFromJSX(spaceAssetsSearchOptions.searchType, 1),
        skip: assetData.length,
        limit: SPACE_ASSETS_GRID_LIMIT,
        ai_suggestions
      },
      assetData,
      (loadingState) => {
        setLoading(loadingState);
        if (!loadingState) {
          setIsInitialLoad(false);
          loadingMoreRef.current = false;
        }
      },
      setAssetData,
      setTotalCounts,
      setIsAPIError,
      setShouldDisabledActionPanel,
      fetchSpaceAssets,
      SPACE_ASSETS_VIEW_TYPE.THUMBNAIL,
      linkedWorkspaces,
      null,
      null,
      validationFilter,
      onSuggestionsFetchSettled,
      disablePrivateAssets,
      lottieAbortRef.current?.signal
    );
  }, [loading, isInitialLoad, sortByData, assetData.length, ai_suggestions, linkedWorkspaces, validationFilter, isMoveTarget, onSuggestionsFetchSettled, disablePrivateAssets]);

  useEffect(() => {
    setIsInitialLoad(true);
    loadingMoreRef.current = false;
    // Cancel any in-flight Lottie sniff when the picker view unmounts (modal close).
    return () => lottieAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (showSelectedAssets) return;

    setIsInitialLoad(true);
    loadingMoreRef.current = false;
    lastLoadedRow.current = 0;

    if (listRef.current) {
      listRef.current.scrollToPosition(0);
    }
    if (queryBuilder.query.$and.length > 0) {
      lottieAbortRef.current?.abort();
      lottieAbortRef.current = new AbortController();
      fetchSpaceAssetsData(
      queryBuilder,
      {
        sortBy: { id: sortByData.id, sortingDirection: sortByData.sortingDirection },
        searchText: spaceAssetsSearchOptions.searchText,
        searchType: extractTextFromJSX(spaceAssetsSearchOptions.searchType, 1),
        skip: 0,
        limit: SPACE_ASSETS_GRID_LIMIT,
        ai_suggestions,
      },
      [],
      (loadingState) => {
        setLoading(loadingState);
        if (!loadingState) {
          setIsInitialLoad(false);
        }
      },
      setAssetData,
      setTotalCounts,
      setIsAPIError,
      setShouldDisabledActionPanel,
      fetchSpaceAssets,
      SPACE_ASSETS_VIEW_TYPE.THUMBNAIL,
      linkedWorkspaces,
      null,
      null,
      validationFilter,
      onSuggestionsFetchSettled,
      disablePrivateAssets,
      lottieAbortRef.current?.signal
    );
    }
  }, [sortByData, props.linkedSpaces, queryBuilder, ai_suggestions, validationFilter, onSuggestionsFetchSettled, disablePrivateAssets]);

  const handleVirtualListScroll = useCallback(
    ({ clientHeight, scrollHeight, scrollTop }: { clientHeight: number; scrollHeight: number; scrollTop: number }) => {
      if (loading || displayData.length >= displayTotalCounts) return;

      const currentRowIndex = Math.floor(scrollTop / scaledRowHeight);
      const visibleRowCount = Math.ceil(clientHeight / scaledRowHeight);
      const visibleItemCount = currentRowIndex * currentItemsPerRow + visibleRowCount * currentItemsPerRow;

      const loadMoreThreshold = displayData.length * LOAD_THRESHOLD;

      if (visibleItemCount >= loadMoreThreshold && lastLoadedRow.current !== currentRowIndex) {
        lastLoadedRow.current = currentRowIndex;
        fetchData();
      }
    },
    [loading, displayData.length, displayTotalCounts, currentItemsPerRow, fetchData, scaledRowHeight]
  );

  const calculateGridDimensions = useCallback(
    (
      containerWidth: number
    ): {
      cardWidthInPixels: number;
      itemsPerRow: number;
    } => {
      const BASE_CARD_WIDTH_REM = 11.25;
      const BASE_GAP_SIZE_REM = 2.5;
      const BASE_FONT_SIZE_PX = 16;

      const cardWidthInPixels = BASE_CARD_WIDTH_REM * displayScaleFactor * BASE_FONT_SIZE_PX;
      const gapSizeInPixels = BASE_GAP_SIZE_REM * displayScaleFactor * BASE_FONT_SIZE_PX;

      const availableContainerWidth = containerWidth;
      const itemsPerRow = Math.max(
        1,
        Math.floor((availableContainerWidth + gapSizeInPixels) / (cardWidthInPixels + gapSizeInPixels))
      );

      return {
        cardWidthInPixels,
        itemsPerRow
      };
    },
    [displayScaleFactor]
  );

  const renderVirtualizedRow = useCallback(
    ({ index, key, style }: { index: number; key: string; style: React.CSSProperties }) => {
      let itemsInCurrentRow = currentItemsPerRow;
      const startItemIndex = index * itemsInCurrentRow;
      const endItemIndex = Math.min(startItemIndex + itemsInCurrentRow, displayTotalCounts || SPACE_ASSETS_GRID_LIMIT);
      const rowItems: ReactNode[] = [];

      if (loading && displayData.length === 0) {
        itemsInCurrentRow = SPACE_ASSETS_GRID_LIMIT_LOADING;
      }

      if ((isInitialLoad && loading) || (loadingMoreRef.current && startItemIndex >= displayData.length)) {
        return (
          <div
            className="ReactVirtualized__Grid__row"
            key={key}
            style={{
              ...style,
              height: 'auto',
              minHeight: scaledRowHeight,
              overflow: 'visible'
            }}>
            <GridSkeleton num={itemsInCurrentRow} />
          </div>
        );
      }

      for (let i = startItemIndex; i < endItemIndex; i++) {
        const assetItem = showSelectedAssets ? spaceAssetsPicked[i] : displayData[i];

        if (assetItem) {
          const isSelected = selectedAssetUids.has(assetItem.uid);
          rowItems.push(
            <AssetThumbnail
              key={`${assetItem.uid}-${(assetItem as any).locale}-${showSelectedAssets}-${isSelected}`}
              {...props}
              rowIndex={index}
              data={assetItem}
              spaceUid={''}
              location={location}
              assetsResData={displayData}
              spaceAssetsPicked={spaceAssetsPicked}
              setSpaceAssetsPicked={setSpaceAssetsPicked}
              breadcrumbClickHandler={breadcrumbClickHandler}
              breadcrumbPath={breadcrumbPath}
              setBreadcrumbPath={setBreadcrumbPath}
              isMultiple={isMoveTarget ? false : isMultiple}
              isSelected={isMoveTarget ? false : isSelected}
              showSelectedAssets={showSelectedAssets}
              localesWithFallback={localesWithFallback}
              spacesWithTitles={spacesWithTitles}
              isMoveTarget={isMoveTarget}
            />
          );
        } else if (i < (displayTotalCounts || SPACE_ASSETS_GRID_LIMIT)) {
          rowItems.push(<AssetLoader key={`skeleton-${i}`} fetch={fetchData} shouldLoad={loading} />);
        }
      }

      return (
        <div
          className="ReactVirtualized__Grid__row"
          key={key}
          id={`ReactVirtualized__Grid__row-${index}`}
          style={{
            ...style,
            height: 'auto',
            minHeight: scaledRowHeight,
            overflow: 'visible'
          }}
          data-test-id={`am-v2-asset-thumbnail-view-row-${index}`}>
          {rowItems}
        </div>
      );
    },
    [
      currentItemsPerRow,
      displayTotalCounts,
      isInitialLoad,
      loading,
      displayData,
      fetchData,
      selectedAssetUids,
      spaceAssetsPicked,
      scaledRowHeight,
      breadcrumbClickHandler,
      breadcrumbPath,
      setBreadcrumbPath,
      isMultiple,
      props,
      showSelectedAssets,
      isMoveTarget
    ]
  );

  return (
    <div
      className={`${(!displayData.length && !loading && !showSelectedAssets) || isAPIError ? 'ThumbnailView--empty' : 'ThumbnailView'}`}
      data-test-id="am-v2-asset-thumbnail-view">

      {(!displayData.length && !loading && !showSelectedAssets) || isAPIError ? (
        <SpaceAssetsEmptyState
          searchApplied={!isEmpty(spaceAssetsSearchOptions.searchText) || appliedFilterCount! > 0}
          isAPIError={isAPIError}
          setShouldClearSearchFilters={setShouldClearSearchFilters}
          handleRetryFetch={fetchData}
          handleGoBack={handleGoBack}
          isSuggestions={isSuggestions}
          isResponsive={isResponsive}
          isMoveTarget={isMoveTarget}
          isUploadTarget={isUploadTarget}
          isNewlyCreatedFolder={isNewlyCreatedFolder}
        />
      ) : (
        <div className="ThumbnailView__grid-wrapper" data-test-id="cs-asset-grid-wrapper" ref={gridWrapperRef}>
          <AutoSizer>
            {({ height, width }: { height: number; width: number }): React.ReactNode => {
              const { itemsPerRow } = calculateGridDimensions(width);
              setCurrentItemsPerRow(itemsPerRow);

              const totalRowCount = Math.ceil(displayTotalCounts / itemsPerRow) + 1;

              return (
                <List
                  ref={listRef}
                  className="List"
                  width={width}
                  height={height}
                  rowCount={totalRowCount}
                  rowHeight={scaledRowHeight}
                  overscanRowCount={5}
                  onScroll={handleVirtualListScroll}
                  rowRenderer={renderVirtualizedRow}
                  style={{ overflow: 'visible' }}
                />
              );
            }}
          </AutoSizer>
        </div>
      )}
    </div>
  );
};

export default React.memo(ThumbnailView);
